import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Domain, DomainCreateAPI, DomainUpdate, Forge } from '../../types';
import { Button } from '../Common/button';
import { Input } from '../Common/input';
import { Label } from '../Common/label';
import { Textarea } from '../Common/textarea';
import { ForgeSelector } from '../Forge';
import { useForgeApiEnabled } from '../../stores/forgeApiStore';

interface DomainFormProps {
  domain?: Domain;
  organizationId?: string | null;  // Used to scope the Forge dropdown when creating a new domain
  onSubmit: (data: DomainCreateAPI | DomainUpdate) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const DomainForm: React.FC<DomainFormProps> = ({
  domain,
  organizationId,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    forge_id: null as string | null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Hidden when Forge is off (databricks build or super-admin kill-switch).
  const forgeApiEnabled = useForgeApiEnabled();

  useEffect(() => {
    if (domain) {
      setFormData({
        name: domain.name,
        description: domain.description || '',
        forge_id: domain.forge_id || null,
      });
    }
  }, [domain]);

  const handleForgeSelect = (forgeId: string | null, forge: Forge | null) => {
    setFormData(prev => ({ ...prev, forge_id: forgeId }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('domainForm.nameErrorRequired');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('domainForm.nameErrorMinLength');
    } else if (formData.name.trim().length > 100) {
      newErrors.name = t('domainForm.nameErrorMaxLength');
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = t('domainForm.descriptionErrorMaxLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        forge_id: formData.forge_id,
      };

      await onSubmit(submitData as DomainCreateAPI | DomainUpdate);
    } catch (error) {
      console.error('Error submitting domain form:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground border-b border-border pb-2">
          {t('domainForm.generalInfo', 'Informations générales')}
        </h2>

        <div className="space-y-2" data-tour="domain-name-field">
          <Label htmlFor="name">
            {t('domainForm.nameLabel')} <span className="text-destructive">{t('domainForm.nameRequired')}</span>
          </Label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder={t('domainForm.namePlaceholder')}
            className={errors.name ? 'border-destructive' : ''}
            disabled={loading}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {t('domainForm.nameHelp')}
          </p>
        </div>

        <div className="space-y-2" data-tour="domain-description-field">
          <Label htmlFor="description">{t('domainForm.descriptionLabel')}</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder={t('domainForm.descriptionPlaceholder')}
            rows={4}
            className={errors.description ? 'border-destructive' : ''}
            disabled={loading}
          />
          {errors.description && (
            <p className="text-sm text-destructive">{errors.description}</p>
          )}
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{t('domainForm.descriptionHelp')}</span>
            <span>{formData.description.length}/500</span>
          </div>
        </div>
      </div>

      {/* Forge Integration Section */}
      {forgeApiEnabled === true && (
      <div className="space-y-4 pt-4 border-t border-border">
        <h2 className="text-lg font-semibold text-foreground pb-2">
          {t('domainForm.forgeSection', 'Intégration Forge')}
        </h2>
        <div className="space-y-2" data-tour="domain-forge-selector">
          <ForgeSelector
            organizationId={domain?.organization_id ?? organizationId ?? null}
            selectedForgeId={formData.forge_id}
            onSelect={handleForgeSelect}
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            {t('domainForm.forgeHelp', 'Liez ce domaine à une Forge pour synchroniser les applications.')}
          </p>
        </div>
      </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          {t('global.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="min-w-[140px]"
          data-tour="domain-submit-btn"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {domain ? t('global.updating') : t('global.creating')}
            </div>
          ) : (
            domain ? t('global.update') : t('domainForm.create')
          )}
        </Button>
      </div>
    </form>
  );
};
